import React, { useState, useEffect } from 'react';
import { Check, Truck, Calendar, Clock, CreditCard, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import SiteContentText from './SiteContentText';
import { correctHomepagePaymentFeature } from '../utils/homepageBookingCopy';

const Features: React.FC = () => {
  const [content, setContent] = useState({
    title: '',
    subtitle: '',
    feature1_title: '',
    feature1_description: '',
    feature2_title: '',
    feature2_description: '',
    feature3_title: '',
    feature3_description: '',
    feature4_title: '',
    feature4_description: '',
    bottom_title: '',
    bottom_description: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase
        .from('site_content')
        .select('key, content')
        .eq('category', 'features');

      if (error) throw error;

      const contentMap = data.reduce((acc, item) => {
        acc[item.key] = item.content;
        return acc;
      }, {} as any);

      setContent({
        title: contentMap.features_title || 'Varför välja oss?',
        subtitle: contentMap.features_subtitle || 'Vi gör det enkelt att hyra professionell festutrustning med fokus på kvalitet och service.',
        feature1_title: contentMap.features_feature1_title || 'Enkelt att boka',
        feature1_description: contentMap.features_feature1_description || 'Välj datum och produkt, så visar vi direkt vad som finns tillgängligt för din fest.',
        feature2_title: contentMap.features_feature2_title || 'Leverans & hämtning',
        feature2_description: contentMap.features_feature2_description || 'Vi levererar och hämtar utrustningen direkt till och från din festlokal.',
        feature3_title: contentMap.features_feature3_title || 'Flexibla hyrtider',
        feature3_description: contentMap.features_feature3_description || 'Hyr för en dag eller en hel helg, du bestämmer vad som passar bäst.',
        feature4_title: contentMap.features_feature4_title || 'Trygga betalningar',
        feature4_description: correctHomepagePaymentFeature(contentMap.features_feature4_description),
        bottom_title: contentMap.features_bottom_title || 'Professionell utrustning, enkelt att använda',
        bottom_description: contentMap.features_bottom_description || 'All vår utrustning kommer färdig att användas med tydliga instruktioner och support vid behov.'
      });
    } catch (err) {
      console.error('Error fetching features content:', err);
      // Keep default values if fetch fails
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <Calendar className="w-8 h-8 text-purple-400" />,
      title: content.feature1_title,
      description: content.feature1_description
    },
    {
      icon: <Truck className="w-8 h-8 text-purple-400" />,
      title: content.feature2_title,
      description: content.feature2_description
    },
    {
      icon: <Clock className="w-8 h-8 text-purple-400" />,
      title: content.feature3_title,
      description: content.feature3_description
    },
    {
      icon: <CreditCard className="w-8 h-8 text-purple-400" />,
      title: content.feature4_title,
      description: content.feature4_description
    }
  ];

  if (loading) {
    return (
      <section className="py-16 md:py-20 border-t border-slate-800">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="bg-slate-900/35 border border-slate-800 p-6 md:p-8 animate-pulse">
            <div className="animate-pulse text-center mb-10">
              <div className="h-8 bg-slate-700 rounded w-48 mx-auto mb-2"></div>
              <div className="h-4 bg-slate-700 rounded w-72 mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-slate-800 border border-slate-700 p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-slate-700 rounded"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-slate-700 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-slate-700 rounded w-full"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5">
                <div className="h-6 bg-slate-700 rounded w-2/3 mb-4" />
                <div className="h-4 bg-slate-700 rounded w-full mb-6" />
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-3 bg-slate-700 rounded w-full" />
                  ))}
                </div>
              </div>
              <div className="lg:col-span-7">
                <div className="aspect-video bg-slate-800 border border-slate-700" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-20 border-t border-slate-800">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="bg-slate-900/35 border border-slate-800 p-6 md:p-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {content.title}
            </h2>
            <SiteContentText
              as="p"
              className="text-slate-400 max-w-2xl mx-auto"
              text={content.subtitle}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {features.map((feature, index) => (
              <article
                key={index}
                className="bg-slate-800/25 border border-slate-700 p-5 flex gap-4 items-start"
              >
                <div className="w-12 h-12 rounded-none bg-slate-900/40 border border-slate-700 flex items-center justify-center text-purple-300 flex-shrink-0">
                  {feature.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <SiteContentText
                    as="p"
                    className="text-slate-400 text-sm leading-relaxed"
                    text={feature.description}
                  />
                </div>
              </article>
            ))}
          </div>

          {/* Value panel: bild som “backdrop”, checklist som overlay */}
          <div className="relative border border-slate-700 bg-slate-950/10 overflow-hidden">
            <img
              src="https://images.pexels.com/photos/2147029/pexels-photo-2147029.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
              alt="DJ equipment"
              className="absolute inset-0 w-full h-full object-cover opacity-60"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/75 via-slate-950/35 to-transparent" />

            <div className="relative p-6 md:p-10 grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-6 bg-slate-900/35 border border-slate-800 p-6 md:p-7">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                  {content.bottom_title}
                </h3>
                <SiteContentText
                  as="p"
                  className="text-slate-400 mb-4"
                  text={content.bottom_description}
                />
                <ul className="space-y-2">
                  {['Enkel installation', 'Förprogrammerade ljusshower', 'Kristallklart ljud', 'Teknisk support'].map((item, i) => (
                    <li key={i} className="flex items-center text-slate-300 text-sm">
                      <Check className="w-4 h-4 text-purple-400 mr-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-6 hidden lg:block">
                <div className="h-full border border-slate-800 bg-slate-900/20" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;